<template>
  <div class="roles-main">
    <el-card shadow="never" class="filter-card">
      <div class="search">
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="Start Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="End Date"
          format="YYYY/MM/DD"
          value-format="YYYY-MM-DD"
          size="small"
        />
        <el-button
          icon="el-icon-search"
          size="small"
          type="success"
          @click="getDeviceStatistics()"
        >
          Search
        </el-button>
        <el-tooltip content="Download" placement="top">
          <el-button
            icon="el-icon-download"
            size="small"
            type="success"
            title="Download"
            @click="download()"
          />
        </el-tooltip>
      </div>
    </el-card>
    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Device Distribution</span>
      </div>
      <div class="chart" v-if="list.records.length > 0">
        <Chart :options="options" />
      </div>
    </el-card>
    <el-card shadow="never" class="section-card">
      <div class="card-head">
        <span class="card-title">Devices</span>
        <span class="card-badge">Total Count : {{ totalCount }}</span>
      </div>
      <el-table
        v-loading="loading"
        :data="list.records"
        ref="table"
        row-key="id"
        size="small"
        lazy
        border
        class="beauty-table"
        empty-text="No data"
        highlight-current-row
        :tree-props="{children: 'children', hasChildren: 'hasChildren'}"
        :cell-class-name="cellGrayWhite"
      >
        <el-table-column
          prop="device"
          label="Device"
          align="left"
          min-width="160"
          show-overflow-tooltip
        />
        <el-table-column
          prop="count"
          label="Count"
          header-align="right"
          align="right"
        >
          <template #default="scope">
            <span class="num-cell">{{ scope.row.count }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="percentage"
          label="Percentage"
          header-align="right"
          align="right"
          min-width="180"
        >
          <template #default="scope">
            <div class="pct-cell">
              <el-progress
                :percentage="pct(scope.row.percentage)"
                :stroke-width="6"
                :show-text="false"
                class="pct-bar"
              />
              <span class="num-cell pct-num">{{ (Number(scope.row.percentage) || 0) }} %</span>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from '@vue/runtime-core'
import { ElMessage } from 'element-plus'
import Chart from '@/components/charts/Charts'
import { getDeviceStats } from '../../../api/report'
import { convertDate, exportExl } from '@/utils/util'
import { cellGrayWhite } from '@/utils/style'
import { buildTreemapOption } from '@/utils/treemap'

const loading = ref(false)

function pct(v) {
  const n = Number(v)
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), 100) : 0
}

const list = reactive({
  records: [],
})

const startDate = ref(convertDate(new Date()))
const endDate = ref(convertDate(new Date()))

const options = reactive({ tooltip: {}, series: [] })

const totalCount = ref(0)

async function download() {
  try {
    const json = [];
    list.records.forEach((item, index) => {
      json.push({ group: item.device, group_count: item.count })
    });
    json.push({ count: totalCount.value });
    const fileName = `${startDate.value}-${endDate.value}.xls`;
    exportExl(json, fileName);
  } catch (e) {
    ElMessage.error('Failed to load data')
  }
}

async function getDeviceStatistics() {
  loading.value = true
  try {
    const { data: res } = await getDeviceStats(startDate.value, endDate.value)
    list.records = res

    if (res.length > 0) {
      totalCount.value = res
        .map(stats => stats.count)
        .reduce((acc, count) => acc + count)

      const r = buildTreemapOption({ records: res, fieldName: 'device', getTotalCount: () => totalCount.value })
      options.tooltip = r.tooltip
      options.series = r.series
    }
  } catch (e) {
    ElMessage.error('Failed to load data')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await getDeviceStatistics()
})
</script>

<style scoped>
.filter-card {
  margin-bottom: 16px;
  border-radius: 8px;
}
.filter-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.search {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
}
.search > *:not(h6) + * {
  margin-left: 20px;
}
.section-card {
  border-radius: 10px;
  margin-bottom: 16px;
}
.section-card :deep(.el-card__body) {
  padding: 16px 18px;
}
.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.card-badge {
  font-size: 12px;
  color: var(--el-text-color-secondary, #909399);
  font-variant-numeric: tabular-nums;
}
.beauty-table :deep(thead th) {
  background: #f5f7fa;
  font-weight: 600;
  color: var(--el-text-color-primary, #303133);
}
.num-cell {
  font-variant-numeric: tabular-nums;
}
.pct-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}
.pct-bar {
  flex: 1;
  min-width: 80px;
}
.pct-num {
  white-space: nowrap;
  color: var(--el-text-color-secondary, #909399);
}

.chart {
  width: 500px;
  height: 400px;
  margin: auto;
}
</style>
